/**
 * Copy Settings Modal Component
 * Sprint 19+: Scene Quick Actions
 * 
 * Allows users to copy layout, background, and script settings
 * from one scene to multiple target scenes
 */

import React, { useState } from 'react';
import { Modal, Checkbox, Space, Typography, Divider, message } from 'antd';
import { Copy } from 'lucide-react';
import { Scene } from '../../../../types/scene';

const { Text } = Typography;

interface CopySettingsModalProps {
  visible: boolean;
  sourceScene: Scene | null;
  allScenes: Scene[];
  onClose: () => void;
  onCopy: (targetSceneIds: string[], settings: CopySettings) => Promise<void>;
}

export interface CopySettings {
  layout: boolean;
  background: boolean;
  script: boolean;
}

export const CopySettingsModal: React.FC<CopySettingsModalProps> = ({
  visible,
  sourceScene,
  allScenes,
  onClose,
  onCopy
}) => {
  const [selectedScenes, setSelectedScenes] = useState<string[]>([]);
  const [settings, setSettings] = useState<CopySettings>({
    layout: true,
    background: true,
    script: false
  });
  const [loading, setLoading] = useState(false);

  // Filter out the source scene from target options
  const targetScenes = allScenes.filter(s => s.id !== sourceScene?.id);

  const handleSceneToggle = (sceneId: string) => {
    setSelectedScenes(prev => 
      prev.includes(sceneId)
        ? prev.filter(id => id !== sceneId)
        : [...prev, sceneId]
    );
  };

  const handleSelectAll = () => {
    if (selectedScenes.length === targetScenes.length) {
      setSelectedScenes([]);
    } else {
      setSelectedScenes(targetScenes.map(s => s.id));
    }
  };

  const handleSettingToggle = (key: keyof CopySettings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopy = async () => {
    if (selectedScenes.length === 0) {
      message.warning('Please select at least one target scene');
      return;
    }

    if (!settings.layout && !settings.background && !settings.script) {
      message.warning('Please select at least one setting to copy');
      return;
    }

    try {
      setLoading(true);
      await onCopy(selectedScenes, settings);
      message.success(`Settings copied to ${selectedScenes.length} scene(s)`);
      handleClose();
    } catch (error) {
      console.error('Failed to copy settings:', error);
      message.error('Failed to copy settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedScenes([]);
    setSettings({ layout: true, background: true, script: false });
    onClose();
  };

  if (!sourceScene) return null;

  return (
    <Modal
      title={
        <Space>
          <Copy size={18} />
          <span>Copy Settings</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      onOk={handleCopy}
      okText="Copy Settings"
      cancelText="Cancel"
      width={600}
      confirmLoading={loading}
      okButtonProps={{ disabled: selectedScenes.length === 0 }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Source Scene Info */}
        <div>
          <Text strong>Copy from:</Text>
          <div style={{ 
            marginTop: 8, 
            padding: 12, 
            background: '#f5f5f5', 
            borderRadius: 6,
            borderLeft: '3px solid #1890ff'
          }}>
            <Text strong style={{ fontSize: 15 }}>{sourceScene.title}</Text>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Scene {sourceScene.order + 1}
              </Text>
            </div>
          </div>
        </div>

        <Divider style={{ margin: 0 }} />

        {/* Settings to Copy */}
        <div>
          <Text strong>Settings to copy:</Text>
          <Space direction="vertical" style={{ marginTop: 8, width: '100%' }}>
            <Checkbox
              checked={settings.layout}
              onChange={() => handleSettingToggle('layout')}
            >
              <Space direction="vertical" size={0}>
                <Text>Layout Preset</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {sourceScene.layoutPreset || 'Default'}
                </Text>
              </Space>
            </Checkbox>

            <Checkbox
              checked={settings.background}
              onChange={() => handleSettingToggle('background')}
            >
              <Space direction="vertical" size={0}>
                <Text>Background</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {sourceScene.background?.type === 'gradient' ? 'Gradient' : sourceScene.background?.type === 'color' ? 'Solid' : sourceScene.background?.type || 'None'}
                </Text>
              </Space>
            </Checkbox>

            <Checkbox
              checked={settings.script}
              onChange={() => handleSettingToggle('script')}
            >
              <Space direction="vertical" size={0}>
                <Text>Script</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {sourceScene.script ? `${sourceScene.script.substring(0, 50)}...` : 'No script'}
                </Text>
              </Space>
            </Checkbox>
          </Space>
        </div>

        <Divider style={{ margin: 0 }} />

        {/* Target Scenes */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text strong>Copy to ({selectedScenes.length} selected):</Text>
            <Checkbox
              onChange={handleSelectAll}
              checked={selectedScenes.length === targetScenes.length && targetScenes.length > 0}
              indeterminate={selectedScenes.length > 0 && selectedScenes.length < targetScenes.length}
            >
              Select All
            </Checkbox>
          </div>

          <div style={{ 
            maxHeight: 300, 
            overflowY: 'auto', 
            border: '1px solid #d9d9d9', 
            borderRadius: 6,
            padding: 8
          }}>
            {targetScenes.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center' }}>
                <Text type="secondary">No other scenes available</Text>
              </div>
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                {targetScenes.map(scene => (
                  <Checkbox
                    key={scene.id}
                    checked={selectedScenes.includes(scene.id)}
                    onChange={() => handleSceneToggle(scene.id)}
                    style={{ width: '100%', padding: 8 }}
                  >
                    <div>
                      <Text strong>{scene.title}</Text>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Scene {scene.order + 1} • {scene.status}
                        </Text>
                      </div>
                    </div>
                  </Checkbox>
                ))}
              </Space>
            )}
          </div>
        </div>
      </Space>
    </Modal>
  );
};

export default CopySettingsModal;
